import { Visualizer, VisualizerConfiguration, VisualizerControl, VisualizationUpdateLevel } from '../data-source/visualizer';
import { Datum } from '../data-source/types';
import { Map as TLMDMap } from '../map/map';
import { Timeline } from '../timeline/timeline';
import { Drawer } from '../panel/cabinets';
import { getDateTimeFormat } from '../application/i18n';
import { delegatedObserver } from '../dom/dom';

import { ObservationDataSource } from './observation';
import { ObservationContainer, ObservationTypeDefinition, CallbackDefinition, EnumMapEntry  } from './container-observation';
enum ChartType {
  Pie = 'pie',
  FilteredPie = 'filteredPie'
}
interface ValueProperties {
  value: string,
  index: number,
  className: string,
  label: string,
  color: string
}

interface ObservationStatisticalVisualizerConfiguration extends VisualizerConfiguration {
  options: {
    sections: {
      label: string | 'auto',
      charts: (ChartDefinition)[],
      chartCount: number
    }[],
    maxSectionChartCount: number
  }
}

interface ChartDefinition {
  type: ChartType,
  label: string,
  propertyName: string,
  value: (datum: Datum) => string,
  map: { [key: string]: number },//maps values to indices
  values: ValueProperties[]
}
interface PieChartDefinition extends ChartDefinition {
}
interface FilteredPieChartDefinition extends ChartDefinition {
  filterBy: string,
  filterValue?: string | string[],
  filterValueLabel: string,
  charts?: PieChartDefinition[],
  filterLabel: string,
  isMultiValueFilter: boolean,
  isFixedFilter: boolean
}
interface Section {
  charts: ChartDefinition[],
  chartCount: number
}
interface ValueDistributionEntry extends ValueProperties {
  count: number,
  percentage: number
}
interface ValueDistribution {
  distribution: { [key: string]: ValueDistributionEntry },
  count: number
}

interface ObservationDatum extends Datum {
  callbacks?: { [key: string]: { result: { [key: string]: any } } }
}
export class ObservationStatisticalVisualizer implements Visualizer {

  private static datTimeFormatOptions: Intl.DateTimeFormatOptions  = { year: 'numeric', month: 'numeric', day: 'numeric', hour: 'numeric', minute: 'numeric' };

  private inspectionContainer: ObservationContainer;
  private source: ObservationDataSource;
  private typeDefinition: ObservationTypeDefinition;

  private configuration: ObservationStatisticalVisualizerConfiguration;


  public constructor(private visualizerKey: string, private obsType: string, private drawer: Drawer) {
    this.inspectionContainer = <ObservationContainer>this.drawer.getContainer();
    this.source = this.inspectionContainer.getDataSource();
    this.typeDefinition = this.inspectionContainer.getDefinition();

    let supportedDefinitions = this.typeDefinition.visualisers.filter(visualizer => visualizer.type === 'statistical');

    this.configuration = supportedDefinitions.length == 0 ? undefined : supportedDefinitions[0];

    if (this.configuration) {

      // preprocess options for an easier experience later
      const callbacksMap: { [key: string]: CallbackDefinition } = this.typeDefinition.callbacks
        ? this.typeDefinition.callbacks.reduce((map, callback) => {
          map[callback.processor] = callback;
          return map;
        }, <{ [key: string]: CallbackDefinition }>{})
        : {};

      // pre-process charts and add charCount property for each section 
      this.configuration.options.sections.forEach(section => {
        // add charCount property for each section
        section.chartCount = section.charts.map((chart: ChartDefinition) => (<FilteredPieChartDefinition>chart).charts ? (<FilteredPieChartDefinition>chart).charts.length : 1).reduce((total, count) => total + count, 0);
        //pre-process all (sub-)charts
        section.charts.forEach(chart => {
          if (chart.type === 'pie') {
            let pieChart = <PieChartDefinition>chart;
            this.enrichChart(callbacksMap, pieChart, pieChart.propertyName);
          } else if (chart.type === 'filteredPie') {
            let filteredPieChart = <FilteredPieChartDefinition>chart;
            this.enrichChart(callbacksMap, filteredPieChart, filteredPieChart.filterBy);
            filteredPieChart.isMultiValueFilter = (filteredPieChart.filterValue !== undefined && (<Array<any>>filteredPieChart.filterValue).push !== undefined);
            filteredPieChart.isFixedFilter = filteredPieChart.filterValue !== undefined && filteredPieChart.filterValue.length > 0;
            // enrich sub charts and concatenate these labels to
            filteredPieChart.charts.forEach(subChart => {
              this.enrichChart(callbacksMap, subChart, subChart.propertyName);
            });
            filteredPieChart.filterLabel = chart.label;
            filteredPieChart.label = '(' + filteredPieChart.charts.map(subChart => subChart.label).join(' / ') + ') ' + supportedDefinitions[0].labels['for'] + ' ' + filteredPieChart.filterLabel;
            // and now adapt the labels for the sub charts
            filteredPieChart.charts.forEach(subChart => {
              subChart.label = subChart.label + ' ' + supportedDefinitions[0].labels['for'] + ' ' + filteredPieChart.filterLabel;
            });

          } else {
            this.enrichChart(callbacksMap, chart, chart.propertyName);
            chart.label = 'Unknown Chart Type';
          }
        })


      });
      // determine the maximum number of charts per section
      this.configuration.options.maxSectionChartCount = Math.max(...this.configuration.options.sections.map(section => section.chartCount));
    }
    //build all markup
    this.build();

    //container will unsubscribe in release cycle
    this.subscribeHandlers();

  }

  public release(): void {
  }

  public key(): string {
    return this.visualizerKey + '_' + this.type();
  }

  public type(): string {
    return this.obsType;
  }

  public setControls(_controls: { [key: string]: VisualizerControl }): Visualizer {
    //no controls here
    return this;
  }

  public setTimeline(_timelinep: Timeline): void {
    //we do not deal with the timeline here
  }

  public setMap(_map: TLMDMap): void {
    //we do not deal with the map here
  }

  public updateSelection(): void {
    // this visualizer cannot select data (yet)
    // an idea is to find the e.g. pie pieces where the belongs to
    // and highlight those.
  }

  public refresh(level: VisualizationUpdateLevel): void {
    if (level <= VisualizationUpdateLevel.filter) {
      let dataList = this.source.getData()[this.type()];
      let data = dataList.getData();

      const start = getDateTimeFormat(ObservationStatisticalVisualizer.datTimeFormatOptions).format(this.source.getMeta().timeRange.visible.start);
      const end = getDateTimeFormat(ObservationStatisticalVisualizer.datTimeFormatOptions).format(this.source.getMeta().timeRange.visible.end);
      const count = data.length;

      this.drawer.getContainerNode().querySelector('div.tlmd-header.tlmd-top .timeRange').textContent = `${start} - ${end}`;
      this.drawer.getContainerNode().querySelector('div.tlmd-header.tlmd-top .count').textContent = String(count);

      this.configuration.options.sections.forEach((section, sectionIndex) => {
        let sectionElem = <HTMLElement>this.drawer.getContainerNode().querySelector(`div.tlmd-sections div.tlmd-content.section-${sectionIndex}`);
        this.drawCharts(data, sectionElem, sectionIndex, section, false);
      });
    }
  }

  /**
  * helper function that enriches the basic chart definition with labels and enum
  * colors/labels values etc.
   */
  private enrichChart(callbacksMap: { [key: string]: CallbackDefinition }, chart: ChartDefinition, pointer: string): void {
    let pointerParts = pointer.split('.');

    if (pointerParts.length > 2 && pointerParts[0] === 'callbacks') {
      let callBackDefinition = callbacksMap[pointerParts[1]];
      chart.label = pointerParts.slice(2).reduce((value, key) => value != null ? value[key] : undefined, callBackDefinition.labels);
      //chart.value = (dataItem: Datum) => pointerParts.slice(2).reduce((value, key) => value != null ? value[key] : undefined, dataItem.properties.callbacks && dataItem.properties.callbacks[pointerParts[1]] ? dataItem.properties.callbacks[pointerParts[1]].result : undefined);
      chart.value = (datum: ObservationDatum) => pointerParts.slice(2)
        .reduce((value, key) => value != null ? value[key] : undefined, datum.callbacks && datum.callbacks[pointerParts[1]] ? datum.callbacks[pointerParts[1]].result : undefined);

      const enumDefinition: EnumMapEntry = pointerParts.slice(2).reduce((value, key) => value != null ? value[key] : undefined, (<any>callBackDefinition).definition.enumMap);
      const values = enumDefinition.values;
      const labels = enumDefinition.labels;
      const colors = enumDefinition.colors;

      chart.map = values.map((value, index) => { return { value: value, index: index } }).reduce((map, entry) => { map[entry.value] = entry.index; return map; }, <any>{});
      chart.values = values.map((value, index) => { return { value: value, index: index, className: `${pointer.replace(/\./g, '_')}_${index}`, label: labels ? labels[index] : "?", color: colors ? colors[index] : "rgb(128,128,128)", } });
    } else {
      chart.label = pointerParts.reduce((value, key) => value != null ? value[key] : undefined, this.typeDefinition.schema.properties).title;
      chart.value = (datum): string => {
        //let value = pointerParts.reduce((value, key) => value != null ? value[key] : undefined, <any>datum.properties);
        let value = pointerParts.reduce((value, key) => value != null ? value[key] : undefined, <any>datum);
        return value != null ? value.toString() : undefined;
      };

      const values: any[] = pointerParts.reduce((value, key) => value != null ? value[key] : undefined, this.typeDefinition.schema.properties).enum;
      const labels: string[] = pointerParts.reduce((value, key) => value != null ? value[key] : undefined, <any>this.typeDefinition.keyMap).labels;
      const colors: string[] = pointerParts.reduce((value, key) => value != null ? value[key] : undefined, <any>this.typeDefinition.keyMap).colors;
      chart.map = values.map((value, index) => { return { value: value, index: index } }).reduce((map: any, entry: { value: any, index: number }) => { map[entry.value] = entry.index; return map; }, <any>{});
      chart.values = values.map((value, index) => { return { value: value, index: index, className: `${pointer.replace(/\./g, '_')}_${index}`, label: labels ? labels[index] : "?", color: colors ? colors[index] : "rgb(128,128,128)", } });
    }
  }
  private build(): void {
    let contentNode = this.drawer.getContainerNode();

    let drawerKey = `${contentNode.parentElement.dataset.cab}${contentNode.parentElement.dataset.name}`;

    const htmlSectionSelectors = (sections: Section[]) =>
      sections.map((section, index) => {
        let sectionLabel = section.charts.map(chart => chart.label).join(" / ");
        return `<a class="tlmd-item tlmd-button btn tlmd-default section-${index} ${index === 0 ? 'tlmd-selected' : ''}" data-drawer="${drawerKey}" data-name="section-${index}" data-section="${index}" href="#" title="${sectionLabel}">${sectionLabel}</a><span class="tlmd-seperator"></span>`
      }).join('');

    const htmlSections = (sections: Section[]) =>
      sections.map((_section, index) => {
        return `<div class="tlmd-content tlmd-chart-section section-${index} ${index === 0 ? 'tlmd-selected' : ''}" data-section="${index}"></div>`
      }).join('');

    // TODO how do we deal with type changes? the tlmd-selectors need to be replaced or are they always not present at this time?
    const selectorHeaderMarkup = `<div class="tlmd-header tlmd-selectors tlmd-multi-row">${htmlSectionSelectors(this.configuration.options.sections)}</div>`;
    contentNode.querySelector('div.tlmd-headers').insertAdjacentHTML('beforeend', selectorHeaderMarkup);
    let sectionsNode = <HTMLDivElement>contentNode.querySelector('div.tlmd-sections');
    //sectionsNode.style.backgroundColor = 'hsla(0, 0%, 17%, 0.8)';
    //    sectionsNode.style.backgroundColor = 'hsla(203, 18%, 75%, 0.9)';
    sectionsNode.innerHTML = htmlSections(this.configuration.options.sections);
  }

  private subscribeHandlers(): void {
    let contentNode = this.drawer.getContainerNode();

    //define this helper for switching chart sections
    const selectContent = (contentName: string): void => {
      // get the selector for given contentName
      let selectorElem = contentNode.querySelector(`div.tlmd-header.tlmd-selectors a.${contentName}`);
      // we only have to do content switching if we are not selected already
      if (!selectorElem.classList.contains('tlmd-selected')) {
        let contentElem = contentNode.querySelector(`div.tlmd-sections div.tlmd-content.${contentName}`);
        // update selectors
        selectorElem.parentNode.querySelectorAll('a.tlmd-button').forEach(btn => btn.classList.remove('tlmd-selected'));
        selectorElem.classList.add('tlmd-selected');
        // update content sections
        contentElem.parentNode.querySelectorAll('div.tlmd-content').forEach(div => div.classList.remove('tlmd-selected'));
        contentElem.classList.add('tlmd-selected');
        // close all pop-ups
        contentElem.parentNode.querySelectorAll('div.tlmd-chart-section > div.tlmd-chart > div.tlmd-popup').forEach((popupDiv: HTMLDivElement) => popupDiv.style.display = 'none');
      }
    }


    // enable switching of sections. we do not use the section switching behaviour of the inspection panel
    this.inspectionContainer.addSubscription(delegatedObserver(contentNode, 'click', 'div.tlmd-header.tlmd-selectors > a.tlmd-button').subscribe(evAndTrgt => {
      evAndTrgt.event.preventDefault();
      //      let index = parseInt(evAndTrgt.target.dataset.section);
      let name = evAndTrgt.target.dataset.name;
      selectContent(name);
      evAndTrgt.target.blur();
    }));

    // enable the pop-ups for changing filter values
    this.inspectionContainer.addSubscription(delegatedObserver(contentNode, 'click', 'div.tlmd-sections > div.tlmd-chart-section > div.tlmd-chart > div.tlmd-chart-header a.tlmd-select-filter').subscribe(evAndTrgt => {
      evAndTrgt.event.preventDefault();
      // retrieve some essential info
      let tlmdChartDiv = <HTMLElement>evAndTrgt.target.closest('div.tlmd-sections > div.tlmd-chart-section > div.tlmd-chart');
      //      let sectionIndex = tlmdChartDiv.dataset.section;
      //      let subChartIndex = tlmdChartDiv.dataset.chart;
      //      let chartIndex = tlmdChartDiv.dataset.parentchart;

      // close pop-ups but open ours
      const popupDiv = tlmdChartDiv.querySelector('div.tlmd-popup');
      tlmdChartDiv.parentNode.parentNode.querySelectorAll('div.tlmd-chart-section > div.tlmd-chart > div.tlmd-popup').forEach((div: HTMLDivElement) => div.style.display = (div === popupDiv ? 'block' : 'none'));
    }));



    // helper for getting selected filter options;
    let getSelectedValues = (tlmdPopupDiv: HTMLDivElement) => Array.from(tlmdPopupDiv.querySelectorAll('div.tlmd-chart-filter-values .tlmd-chart-filter-value.checked')).map((valueElem: HTMLElement) => valueElem.dataset.value);

    // helper for updating the chart(s) based on seleced filter
    // option(s)
    let closePopup = (tlmdPopupDiv: HTMLDivElement) => {
      let sectionIndex = parseInt(tlmdPopupDiv.dataset.section);
      let chartIndex = parseInt(tlmdPopupDiv.dataset.chart);
      let chart = <FilteredPieChartDefinition>this.configuration.options.sections[sectionIndex].charts[chartIndex];

      let selectedValues = getSelectedValues(tlmdPopupDiv);
      if (selectedValues.length === 0) {
        // oopsy nothing selected, take first
        selectedValues = [chart.values[0].value.toString()];
      }

      if (chart.isMultiValueFilter) {
        chart.filterValue = selectedValues;
      } else {
        chart.filterValue = selectedValues[0];
      }
      //console.log('chart.filterValue = ',selectedValues);

      // close pop-up (markup gets destroyed any way but maybe in the
      // future......)
      tlmdPopupDiv.style.display = 'none';

      //update statistics
      this.refresh(VisualizationUpdateLevel.settings);
    }


    this.inspectionContainer.addSubscription(delegatedObserver(contentNode, 'click', 'div.tlmd-sections > div.tlmd-chart-section > div.tlmd-chart > div.tlmd-popup div.tlmd-chart-filter-values .tlmd-chart-filter-value').subscribe(evAndTrgt => {
      evAndTrgt.event.preventDefault();
      let tlmdPopupDiv = <HTMLDivElement>evAndTrgt.target.closest('div.tlmd-popup');
      let sectionIndex = parseInt(tlmdPopupDiv.dataset.section);
      let chartIndex = parseInt(tlmdPopupDiv.dataset.chart);
      let chart = <FilteredPieChartDefinition>this.configuration.options.sections[sectionIndex].charts[chartIndex];

      if ((chart).isMultiValueFilter) {
        // toggle the checked status
        evAndTrgt.target.classList.toggle('checked');
        evAndTrgt.event.stopImmediatePropagation();
      } else {
        // check the value and close
        evAndTrgt.target.parentNode.querySelectorAll('.tlmd-chart-filter-value').forEach(valueElem => valueElem.classList.remove('checked'));
        evAndTrgt.target.classList.add('checked');
        closePopup(tlmdPopupDiv);
      }
    }));

    // close a multiValueFilter popup
    this.inspectionContainer.addSubscription(delegatedObserver(contentNode, 'click', 'div.tlmd-sections > div.tlmd-chart-section > div.tlmd-chart > div.tlmd-popup a.tlmd-popup-close, div.tlmd-sections > div.tlmd-chart-section > div.tlmd-chart > div.tlmd-popup').subscribe(evAndTrgt => {
      evAndTrgt.event.preventDefault();
      if (!evAndTrgt.target.classList.contains('tlmd-chart-filter-value')) {
        let tlmdPopupDiv = <HTMLDivElement>evAndTrgt.target.closest('div.tlmd-popup');
        closePopup(tlmdPopupDiv);
      }
    }));
  }

  private drawCharts(data: Datum[], node: HTMLElement, sectionIndex: number, section: Section, hidden: boolean): void {
    if (node) {
      //            let count = dataItems.length;
      //          let chartDimensions = this.chartWidthAndHeight(section.chartCount);
      //          let minSize = Math.max(chartDimensions.width, chartDimensions.height);
      //          if (navigator.userAgent.indexOf('Edge') !== -1) {
      //          minSize *= 0.9;
      //          }

      node.innerHTML = '';

      // helper function that appends the svgMarkup
      let appendChart = (sectionIndex: number, chartIndex: number, chart: ChartDefinition, valueDistribution: ValueDistribution, svgMarkup: string, parentChartIndex?: number, parentChart?: FilteredPieChartDefinition) => {
        let chartMarkup = `
                    <div class="tlmd-chart" data-section="${sectionIndex}" data-chart="${chartIndex}" ${parentChartIndex === undefined ? '' : `data-parentchart="${parentChartIndex}"`}>
                    <div class="tlmd-chart-item tlmd-chart-header">
                    <span class="title">${chart.label}${parentChart ? ': ' + (parentChart.isFixedFilter ? parentChart.filterValueLabel : `<a class="tlmd-button tlmd-default btn tlmd-select-filter" href="#">${parentChart.filterValueLabel}</a>`) : ''}</span>
                    <span class="count">${valueDistribution.count} ${this.configuration.labels['items']}</span>
                    </div>
                    <div class="tlmd-chart-item tlmd-diagram">
                    <div class="tlmd-chart-item tlmd-graphic">${svgMarkup}</div>
                    <div class="tlmd-chart-item tlmd-chart-legend">${chart.values.map((value) => `<div class="tlmd-legend-item"><div class="color" style="background-color:${value.color};">&nbsp;</div><div class="title"><span>${value.label} (<span class="value">${(valueDistribution.distribution[value.value].percentage * 100).toFixed(1)}% | ${valueDistribution.distribution[value.value].count}</span>)<span></div></div>`).join('')
          }</div>
                    </div>
                    ${(parentChart && !parentChart.isFixedFilter ?
            `<div class="tlmd-popup" data-section="${sectionIndex}" data-chart="${parentChartIndex}" data-subchart="${chartIndex}">
                    <div classs="filter-title">${parentChart.filterLabel}:</div> ${parentChart.isMultiValueFilter ? '<a class="tlmd-popup-close" href="#">X</a>' : ''}
                    <div class="tlmd-chart-filter-values">
                    ${parentChart.values.map((value) => `<div class="tlmd-chart-filter-value ${(parentChart.isMultiValueFilter ? parentChart.filterValue.indexOf(value.value) != -1 : parentChart.filterValue === value.value) ? 'checked' : ''}" data-value="${value.value}">${value.label}</div>`).join('')
            }  </div> 
                    </div>`
            : '')}
                    </div>
                    `;
        node.insertAdjacentHTML('beforeend', chartMarkup);
      };

      if (!hidden) {
        // add all charts in this section
        section.charts.forEach((chart, chartIndex) => {
          if (chart.type === 'pie') {
            const valueDistribution = this.calculateValueDistributions(data, chart)[0];
            const svgMarkup = this.svgPieChart(valueDistribution);
            appendChart(sectionIndex, chartIndex, chart, valueDistribution, svgMarkup);
          } else if (chart.type === 'filteredPie') {
            // set filterValue if that hasn't been done (by fixed
            // value(s) in definition or by manual select)
            if (!(<FilteredPieChartDefinition>chart).filterValue || (<FilteredPieChartDefinition>chart).filterValue.length === 0) {
              (<FilteredPieChartDefinition>chart).filterValue = (<FilteredPieChartDefinition>chart).isMultiValueFilter ? [chart.values[0].value] : chart.values[0].value;
            }
            // set filterValue label
            (<FilteredPieChartDefinition>chart).filterValueLabel = (<Array<string>>(<FilteredPieChartDefinition>chart).filterValue).push ?
              (<Array<string>>(<FilteredPieChartDefinition>chart).filterValue).map(filterValue => chart.values[chart.map[filterValue]].label).toString() :
              chart.values[chart.map[<string>(<FilteredPieChartDefinition>chart).filterValue]].label;

            const valueDistributions = this.calculateValueDistributions(data, chart);
            valueDistributions.forEach((valueDistribution, index) => {
              const svgMarkup = this.svgPieChart(valueDistribution);
              appendChart(sectionIndex, index, (<FilteredPieChartDefinition>chart).charts[index], valueDistribution, svgMarkup, chartIndex, <FilteredPieChartDefinition>chart);
            });
          } else {
            // unknown so skip
            console.log('Skipper chart with unknown type %s', chart.type);
          }

        });
      }
    }
  }

  private calculateValueDistributions(data: Datum[], chart: ChartDefinition) {
    // determine which filter to apply to the set of dataItems
    const pass = (<FilteredPieChartDefinition>chart).filterBy &&
      (<FilteredPieChartDefinition>chart).filterValue
      ? (<Array<string>>(<FilteredPieChartDefinition>chart).filterValue).push
        ? (datum: Datum) => (<FilteredPieChartDefinition>chart).filterValue.indexOf(chart.value(datum)) != -1
        : (datum: Datum) => (<FilteredPieChartDefinition>chart).filterValue === chart.value(datum)
      : (_datum: Datum) => true;
    // actually select the dataItems to use (can be the complete set)
    const selectedDataItems = data.filter(pass);
    // calculate all value distributions for this chart (can be multiple
    // distributions that have all been based on the same filtered data set)
    let valueDistributions = (<FilteredPieChartDefinition>chart).charts ? (<FilteredPieChartDefinition>chart).charts.map(subChart => this.calculateFilteredValueDistribution(selectedDataItems, subChart)) : [this.calculateFilteredValueDistribution(selectedDataItems, chart)];
    // return an array with all distributions
    return valueDistributions;
  }

  private calculateFilteredValueDistribution(data: Datum[], chart: ChartDefinition): ValueDistribution {
    let totalCount = data.length;

    let valueDistribution: { [key: string]: ValueDistributionEntry } = chart.values
      .map((valueProperties) => <ValueDistributionEntry>Object.assign({ count: 0, percentage: 0 }, valueProperties))
      .reduce((map: { [key: string]: ValueDistributionEntry }, entry: ValueDistributionEntry) => { map[entry.value] = entry; return map; }, {});

    if (data.length > 0) {
      // count the number of occurrences for each (enum) value
      data.forEach(datum => {
        const value = chart.value(datum);
        if (valueDistribution[value]) {
          valueDistribution[value].count++;
        } else {
          totalCount--;
        }
      });

      // calculate the percentages
      Object.values(valueDistribution)
        // .filter( (entry:ValueDistributionEntry) => entry.count > 0 )
        .forEach((entry: ValueDistributionEntry) =>
          entry.percentage = entry.count / totalCount
        );
    }
    return { distribution: valueDistribution, count: totalCount };
  }

  private svgPieChart(valueDistribution: ValueDistribution): string {
    let width: number = 1024;
    let height: number = 1024;
    let processedItems = Object.values(valueDistribution.distribution).filter(value => value.count > 0);

    const totalSize = Math.min(width, height);
    const center = totalSize / 2.0;

    const pieScale = 1.0;

    const totalPieRadius = center * pieScale;
    const pointOnCircle = (centerX: number, centerY: number, radius: number, angle: number): { x: number, y: number } => {
      let x = Math.round(centerX + (Math.cos(angle) * radius));
      let y = Math.round(centerY + (Math.sin(angle) * radius));
      return { x, y };
    };


    let svg = `<svg viewBox="0 0 ${width} ${height}" class="tlmd-pie${processedItems.length === 0 ? '-nodata' : ''}" >`;

    let paths = '';
    if (processedItems.length === 0) {// special case: no data
      // empty circle with message
      svg += `<circle r="${totalPieRadius}" cx="${center}" cy="${center}" />
                <text class="tlmd-message" text-anchor="middle" x="${center}" y="${center}" >${this.configuration.labels['noItems']}</text>`;

    } else if (processedItems.length === 1) {// special case: all data items have same value
      // one item takes up 100%, svg arc can't do full arcs, draw a circle
      // instead
      svg += `<g data-entry-id="${processedItems[0].className}" class="${processedItems[0].className} highlightGroup pieGroup"><circle cx="${center}" cy="${center}" r="${totalPieRadius}" fill="${processedItems[0].color}"/></g>`;
    } else {// the normal case: a distribution of data items over multiple
      // enum values
      let currentAngle = 0.0;
      processedItems.forEach(item => {

        const itemAngle = item.percentage * 2.0 * Math.PI;

        const start = pointOnCircle(center, center, totalPieRadius, currentAngle);
        const end = pointOnCircle(center, center, totalPieRadius, currentAngle + itemAngle);

        paths += this.svgPieSlice(
          center,
          center,
          start.x,
          start.y,
          end.x,
          end.y,
          totalPieRadius,
          itemAngle > Math.PI ? 1 : 0,
          item.color,
          item.className
        );
        currentAngle += itemAngle; // step along the circle
      });
    }
    return svg + `${paths}</svg>`;
  }

  private svgPieSlice(centerX: number, centerY: number, arcStartX: number, arcStartY: number, arcEndX: number, arcEndY: number, radius: number, largeArcFlag: number, color: string, className: string): string {
    return `<g data-entry-id="${className}" class="${className} highlightGroup pieGroup"><path d="M${centerX} ${centerY} L${arcStartX} ${arcStartY} A ${radius} ${radius} 0 ${largeArcFlag} 1 ${arcEndX} ${arcEndY} z" class="pieSlice" fill="${color}"/></g>`;
  }
}